import axios from "axios";
import { makeAutoObservable } from "mobx";
import { IUser } from "../models/IUser";
import { authResponse } from "../models/response/authResponse";
import AuthService from "../services/AuthService";
import UserService from "../services/UserService";
import { Certification, CertificationApplication } from "../models/Certification";
import { Applicant, Option } from "../models/General";

export default class Store {
  user = {} as IUser;
  users = [] as IUser[];
  applicant = {} as Applicant;
  isAuth = false;
  isLoading = true;
  redirectPath: string | null = null;
  certification = {} as Certification;
  isAdmin: any;
  certifications = [] as Certification[];
  certificationsLoaded = false;
  news: any;
  certifiedUsers: any;
  degreesOptions = [] as Option[];
  rankOptions = [] as Option[];
  applications = [] as CertificationApplication[];

  constructor() {
    makeAutoObservable(this);
  }

  setAuth(bool: boolean) {
    this.isAuth = bool;
  }

  setUser(user: IUser) {
    this.user = user;
  }

  setUsers(users: IUser[]) {
    this.users = users;
  }

  setApplicant(applicant: Applicant) {
    this.applicant = applicant;
  }

  setLoading(bool: boolean) {
    this.isLoading = bool;
  }

  setRedirectPath(path: string | null) {
    this.redirectPath = path;
  }

  setSertifications(certifications: Certification[]) {
    this.certifications = certifications;
    this.certificationsLoaded = true;
  }

  setApplications(applications: CertificationApplication[]) {
    this.applications = applications;
  }

  async getUsers() {
    try {
      const response = await UserService.fetchUsers();
      this.setUsers(response.data);
      return this.users as IUser[];
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async login(email: string, password: string) {
    try {
      const response = await AuthService.login(email, password);
      localStorage.setItem("token", response.data.accessToken);
      this.setAuth(true);
      this.setUser(response.data.user);
      this.setApplicant(response.data.applicant);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response;
        }
      } else {
        console.log(error);
      }
    }
  }

  async logout() {
    try {
      await AuthService.logout();
      localStorage.removeItem("token");
      this.setAuth(false);
      this.setUser({} as IUser);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async registration(
    email: string,
    password: string,
  ) {
    try {
      const response = await AuthService.registration(
        email,
        password
      );
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response;
        }
      } else {
        console.log(error);
      }
    }
  }

  async restoreLink(email: string) {
    try {
      const response = await AuthService.restoreLink(email);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async updatePassword(id: string, password: string, passwordNew: string) {
    try {
      await AuthService.updatePassword(id, password, passwordNew);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async activateUser(email: string, isActivated: boolean, user: IUser) {
    try {
      await AuthService.activateUser(email, isActivated, user);
      this.getUsers();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async updateUser(data: Applicant) {
    try {
      const response = await AuthService.updateUser(data, this.user.id);
      this.setApplicant(response.data.applicant);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error("Помилка оновлення профілю:", error.response.data.message);
        }
      } else {
        console.error("Помилка:", error);
      }
    }
  }

  async restorePassword(password: string, link: string) {
    try {
      const response = await AuthService.restorePassword(password, link);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async getActivationLink() {
    try {
      await UserService.getLink();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async checkAuth() {
    this.setLoading(true);
    try {
      const response = await axios.get<authResponse>(`${process.env.REACT_APP_BASE_URL}/refresh`, {
        withCredentials: true,
      });
      localStorage.setItem("token", response.data.accessToken);
      this.setAuth(true);
      this.setUser(response.data.user);
      this.applicant = response.data.applicant;
    } catch (error) {
      this.setAuth(false);
      localStorage.removeItem("token");
      console.log(error);
    } finally {
      this.setLoading(false);
    }
  }

  async getDegreesOptions() {
    try {
      const response = await UserService.getDegreesOptions();
      this.degreesOptions = response.data;
      return this.degreesOptions;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async getRankOptions() {
    try {
      const response = await UserService.getRankOptions();
      this.rankOptions = response.data;
      return this.rankOptions;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async checkCertificationApplicationStatus(certificationId: string | undefined) {
    throw new Error('Method not implemented.');
  }

  async getApplications() {
    try {
      const response = await UserService.fetchApplications();
      this.setApplications(response.data);
      return this.applications;
    } catch (error) {
      console.log(error);
    }
  }

  async getUserApplications(userId: string) {
    try {
      const response = await UserService.fetchUserApplications(userId);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error("Помилка завантаження заявок користувача:", error.response.data.message);
        }
      } else {
        console.error("Помилка:", error);
      }
    }
  }

  async getCertifications() {
    try {
      const response = await UserService.fetchCertifications();
      this.setSertifications(response.data);
      return this.certifications;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async updateCertification(id: string, data: Certification) {
    try {
      const response = await UserService.updateCertification(id, data);
      if (response && response.status === 200) {
        const index = this.certifications.findIndex(cert => cert.id === Number(id));
        if (index !== -1) {
          this.certifications[index] = { ...this.certifications[index], ...data };
        }
      }
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async createCertification(data: Certification) {
    try {
      const response = await UserService.createCertification(data);
      if (response && response.status === 200) {
        // Assuming response.data contains the new certification object
        this.certifications.push(response.data[0]);
      }
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async fetchCertification(id: string) {
    try {
      const response = await UserService.fetchCertification(id);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async applyForCertification(certificationId: number, formData: FormData) {
    try {
      const response = await UserService.applyForCertification(certificationId, formData);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error(error.response.data.message);
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async getApplicationById(id: string) {
    try {
      const response = await UserService.fetchApplicationById(id);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }

  async updateApplicationStatus(id: string, updatedApplication: CertificationApplication) {
    try {
      const response = await UserService.updateApplicationStatus(id, updatedApplication);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          return error.response.data.message;
        }
      } else {
        console.log(error);
      }
    }
  }
}
